import {useContext, useEffect} from 'react';
import {NavigationFocusContext} from '@header/scripts/components/context/NavigationFocusContext.jsx';

const NAVIGATION_CONTAINER_SELECTOR = '.navigation__container-new';
// scoped to the direct children so the hidden layer of an active item cannot be matched
const FIRST_LEVEL_LINK_SELECTOR = ':scope > div > .navigation__item-label';
const OPEN_BUTTON_SELECTOR = ':scope > div > .navigation__open-button';

const isFocusInsideNavigation = () => !!document.activeElement?.closest(NAVIGATION_CONTAINER_SELECTOR);

/**
 * Keyboard handling for the opened desktop navigation layer:
 * - Tab moves the focus into the layer while the layer is only opened by hovering (WCAG 2.1.1)
 * - Escape closes the layer and returns the focus to the first level item (WCAG 2.1.2)
 */
export const useNavigationKeyboard = (currentHoveredItemId, closeLayer) => {
    const {toSecondLevel, reset} = useContext(NavigationFocusContext);

    useEffect(() => {
        if (!currentHoveredItemId) {
            return;
        }

        const firstLevelItem = document.querySelector(`[data-navigation-item-id="${currentHoveredItemId}"]`);

        const closeAndRestoreFocus = () => {
            const restoreFocus = isFocusInsideNavigation();
            closeLayer();
            reset();
            if (restoreFocus) {
                firstLevelItem?.querySelector(FIRST_LEVEL_LINK_SELECTOR)?.focus();
            }
        };

        const moveFocusIntoLayer = (event) => {
            // as long as the focus is still outside, the layer was opened by hovering only
            if (isFocusInsideNavigation()) {
                return;
            }

            const openButton = firstLevelItem?.querySelector(OPEN_BUTTON_SELECTOR);
            if (!openButton) {
                return;
            }

            event.preventDefault();
            toSecondLevel(openButton);
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeAndRestoreFocus();
            } else if (event.key === 'Tab' && !event.shiftKey) {
                moveFocusIntoLayer(event);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [currentHoveredItemId, closeLayer]);
};
